/* eslint-disable react-refresh/only-export-components */
import React from "react";
import {
    getCellProperty,
    isAlphaNumericKey,
    isNavigationKey,
    keyCodes,
    type Cell,
    type CellTemplate,
    type Compatible,
    type Uncertain,
    type UncertainCompatible,
} from "@silevis/reactgrid";

export interface FormulaGridCell extends Cell {
    type: "formula";
    formula: string;
    rawValue?: string | number;
}

export class FormulaCellTemplate implements CellTemplate<FormulaGridCell> {
    getCompatibleCell(
        uncertainCell: Uncertain<FormulaGridCell>,
    ): Compatible<FormulaGridCell> {
        const formula = getCellProperty(uncertainCell, "formula", "string");

        // Grab 'value' from the mapper even if it isn't strictly defined locally
        // to handle the generic engine output.
        let rawValue: string | number = "";
        const incomingValue = (uncertainCell as any).value;
        if (
            typeof incomingValue === "string" ||
            typeof incomingValue === "number"
        ) {
            rawValue = incomingValue;
        }

        const valueNum =
            typeof rawValue === "number"
                ? rawValue
                : parseFloat(rawValue as string);
        const style = uncertainCell.style;
        const nonEditable = uncertainCell.nonEditable;

        return {
            type: "formula",
            formula: formula || "",
            value: isNaN(valueNum) ? 0 : valueNum,
            text: String(rawValue),
            rawValue: rawValue,
            style: style,
            nonEditable: nonEditable || false,
        };
    }

    handleKeyDown(
        cell: Compatible<FormulaGridCell>,
        keyCode: number,
        ctrl: boolean,
        _shift: boolean,
        alt: boolean,
        key?: string,
        _capsLock?: boolean,
    ): { cell: Compatible<FormulaGridCell>; enableEditMode: boolean } {
        if (cell.nonEditable) {
            return { cell, enableEditMode: false };
        }
        if (ctrl || alt) {
            return { cell, enableEditMode: false };
        }
        if (isAlphaNumericKey(keyCode)) {
            const updatedCell = this.update(cell, {
                ...cell,
                formula: key || "",
            });
            return {
                cell: { ...updatedCell, formula: String(updatedCell.formula) },
                enableEditMode: true,
            };
        }
        return {
            cell,
            enableEditMode:
                keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
        };
    }

    update(
        cell: Compatible<FormulaGridCell>,
        cellToMerge: UncertainCompatible<FormulaGridCell>,
    ): Compatible<FormulaGridCell> {
        return this.getCompatibleCell({ ...cell, ...cellToMerge });
    }

    render(
        cell: Compatible<FormulaGridCell>,
        isInEditMode: boolean,
        onCellChanged: (
            cell: Compatible<FormulaGridCell>,
            commit: boolean,
        ) => void,
    ): React.ReactNode {
        return (
            <div
                style={{
                    padding: "0 4px",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {isInEditMode && !cell.nonEditable ? (
                    <Input cell={cell} onCellChanged={onCellChanged} />
                ) : (
                    <div>{cell.rawValue ?? cell.text}</div>
                )}
            </div>
        );
    }
}

const Input = ({
    cell,
    onCellChanged,
}: {
    cell: Compatible<FormulaGridCell>;
    onCellChanged: (cell: Compatible<FormulaGridCell>, commit: boolean) => void;
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onCellChanged({ ...cell, formula: e.currentTarget.value }, false);
    };
    const commitValue = (value: string) => {
        onCellChanged({ ...cell, formula: value }, true);
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        commitValue(e.currentTarget.value);
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            isAlphaNumericKey(e.keyCode) ||
            (isNavigationKey(e.keyCode) && e.keyCode !== keyCodes.ENTER)
        )
            e.stopPropagation();
    };
    return (
        <div style={{ height: "100%", width: "100%", padding: 0 }}>
            <input
                style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                }}
                autoFocus
                onChange={handleChange}
                defaultValue={cell.formula}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onCopy={(e) => e.stopPropagation()}
                onCut={(e) => e.stopPropagation()}
                onPaste={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
            />
        </div>
    );
};
